/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, lazy, Suspense } from 'react'
import type { PageProps } from 'gatsby'
import { graphql } from 'gatsby'
import { GatsbySeo, BreadcrumbJsonLd } from 'gatsby-plugin-next-seo'
import { Statsig } from 'src/sdk/statsig'
import { ProductProvider } from 'src/components/restructure/product/contexts/product-context'
import { CustomerReviews } from 'src/components/restructure/product/sections/CustomerReviews'
import { SellerInfo } from 'src/components/restructure/product/sections/SellerInfo'
import { TabbedNavigation } from 'src/components/restructure/product/sections/TabbedNavigation'
import { GalleryImage } from 'src/components/restructure/product/sections/GalleryImage'
import { Sidebar } from 'src/components/restructure/product/sections/Sidebar'
import { ProductTitle } from 'src/components/restructure/product/sections/Sidebar/ProductTitle'
import type {
  Product,
  ProgressiveDiscount,
} from 'src/components/restructure/product/types/product'
import { mapperBreadcrumbList } from 'src/utils/mapperProducts'
import Breadcrumb from 'src/components/ui/Breadcrumb'
import { RecommendationProvider } from 'src/components/restructure/product/contexts/Recommendations/recommendations-context'
import { TalkSpecialist } from 'src/components/restructure/product/sections/TalkSpecialist'
import { CommercialBenefits } from 'src/components/restructure/product/sections/CommercialBenefits'
import { MasterDataLogger } from 'src/errors/logger'
import {
  makeProductDetailEvent,
  makeViewItemEvent,
  sendEvent,
} from 'src/utils/restructure/analytics'
import { checkEnviromentIsB2B } from 'src/utils/checkEnviroment'
import GenericShelfv2 from 'src/components/restructure/product/shelves/GenericShelfV2'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getInitialItem } from 'src/components/restructure/utils/get-initial-sku'
import { dispatchCepPersistedAppliedEvent } from 'src/utils/amplitude'
import { getProductQuery } from 'src/graphql/queries/product/getProduct'
import type { ProductPageQuery } from '@generated/graphql'
import { StickyBuyBar } from 'src/components/restructure/product/sections/StickyBuyBar'
import { MusePdpEntryPoint } from 'src/components/muse/MusePdpEntryPoint'
import { ProductStripe } from 'src/components/global/ProductStripe'
import type { StripeDataType } from 'src/cms/hooks/getProductStripeData'
import { getProductStripeData } from 'src/cms/hooks/getProductStripeData'
import { dynamicYieldContextPage } from 'src/utils/dynamicYield/dynamicYieldContextPage'
import { getGiftCardComponentsData } from 'src/components/GiftCard/hooks/getGiftCardComponentsData'
import { GalleryFlags } from 'src/components/restructure/product/sections/GalleryImage/GalleryFlags'
import ProductDetailsHighlight from 'src/components/restructure/product/sections/ProductDetailsHighlight/ProductDetailsHighlight'
import { getCMSPage } from 'src/utils/getCMSPage'
import { GiftCardProvider } from 'src/components/GiftCard/context/useGiftCardContext'
import { useMobile } from 'src/hooks/useMobile'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'
import { SphereContent } from 'src/components/restructure/product/sections/SphereContent'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { ProductInformationSection } from 'src/components/restructure/product/sections/ProductInformationSection/ProductInformationSection'
import { ProductCustomHtml } from 'src/components/restructure/product/sections/ProductCustomHtml'
import { handleSSRError } from 'src/utils/handleSSRError'
import { retryTransient } from 'src/utils/pdp/retryTransient'
import { toGraphqlString } from 'src/utils/pdp/toGraphqlString'
import { sendToSplunk } from 'src/utils/sendToSplunk'
import CrossSellV2 from 'src/components/restructure/product/sections/CrossSellV2/CrossSellV2'
import { InfiniteProductList } from 'src/components/restructure/product/sections/InfiniteProductList'
import {
  REVIEWS_EXPERIMENT_ID,
  PDP_FIRST_FOLD_REVAMP_EXPERIMENT_ID,
} from 'src/components/common/StatsigExposure/StatsigExposure'
import { ALBUM_PROMO_COLLECTION, isAlbumPage } from 'src/utils/pdp/isAlbumPage'
import { PAGE_TYPE_MAP } from 'src/hooks/usePageType'
import { buildQueryParams } from 'src/utils/buildQueryParams'
import type { GetKitlooksResponse } from 'src/types/kitlooks'
import { GoToKitlookWidget } from 'src/components/KitlookWidget'
import { KitlookWidget } from 'src/components/KitlookWidget/KitlookWidget'
import { ProductGroupJsonLd } from 'src/components/restructure/product/sections/ProductGroupJsonLd/ProductGroupJsonLd'
import getCategoryRedirectPathByProductSlug from 'src/utils/pdp/getCategoryRedirectPathByProductSlug'

import { axiosClient, clientStore } from '../../services/axiosInstance'

// Lazy-loaded: only rendered for the Album Copa promo collection or the
// hardcoded gift card product (productId '11113'), not the regular PDP path.
const AlbumCopaTemplate = lazy(() =>
  import('src/templates/pdp/AlbumCopa').then((module) => ({
    default: module.AlbumCopaTemplate,
  }))
)

const SidebarGiftCard = lazy(() =>
  import('src/components/GiftCard/components/SidebarGiftCard').then(
    (module) => ({ default: module.SidebarGiftCard })
  )
)

const GiftCardSelectedImage = lazy(() =>
  import(
    'src/components/GiftCard/components/GiftCardSelectedImage/GiftCardSelectedImage'
  ).then((module) => ({ default: module.GiftCardSelectedImage }))
)

const GiftCardImages = lazy(() =>
  import('src/components/GiftCard/components/GiftCardImages').then(
    (module) => ({ default: module.GiftCardImages })
  )
)

const GiftCardComponents = lazy(() =>
  import('src/components/GiftCard/components/GiftCardComponents').then(
    (module) => ({ default: module.GiftCardComponents })
  )
)

interface CustomCMSSection {
  id: string
  name: string
  data: Record<string, any>
}

export interface CustomCMSData {
  id: string
  name: string
  type: string
  status: string
  versionId: string
  versionStatus: string
  sections: CustomCMSSection[]
}

export type ServerDataProductPage = {
  giftCardComponentsData?: any
  stripeData: StripeDataType
  sphereData: string
  product: Product
  skuId?: string
  progressiveDiscount?: ProgressiveDiscount
  kitlooks?: GetKitlooksResponse | null
  albumCMSData?: {
    props?: {
      cmsData: CustomCMSData
    }
  }
  LearnMoreBikeInsuranceData?: {
    props?: {
      cmsData: CustomCMSData
    }
  }
}

interface ProductPageProps extends PageProps {
  serverData: ServerDataProductPage
  data: ProductPageQuery
}

const isB2B = checkEnviromentIsB2B()

function ProductPage({
  serverData,
  data,
  location: { pathname },
}: ProductPageProps) {
  const bikeInsuranceCms: CustomCMSData | undefined =
    serverData?.LearnMoreBikeInsuranceData?.props?.cmsData

  const { site } = data
  const giftCardComponentsData = serverData?.giftCardComponentsData

  const isReviewDisplayExperimentEnabled = Statsig.useStatsigFeatureGateValue(
    REVIEWS_EXPERIMENT_ID
  )

  const progressiveDiscount = serverData?.progressiveDiscount
  const product = serverData?.product

  const sphereData = serverData?.sphereData

  const skuId = serverData?.skuId ?? null

  const { postalCode } = useAddressContext()
  const { isMobile } = useMobile()
  const { pageData } = useGlobalContext()

  const isPdpFirstFoldRevampEnabled = Statsig.useStatsigFeatureGateValue(
    PDP_FIRST_FOLD_REVAMP_EXPERIMENT_ID
  )

  const productNotFound = !product

  const memoInitialItem = useMemo(() => {
    if (!product) {
      return undefined
    }

    return getInitialItem(product.items, product.slug, skuId)
  }, [product, skuId])

  useEffect(() => {
    if (!product?.items?.length) {
      return
    }

    dynamicYieldContextPage({
      page: 'PRODUCT',
      data: [memoInitialItem!.itemId],
    })
  }, [memoInitialItem, product, productNotFound, skuId, product?.slug])

  const memoProduct = useMemo(() => {
    if (!product) {
      return undefined
    }

    return {
      product: {
        ...product,
        items: product.items.filter((item) => item !== null),
      },
      progressiveDiscount,
      slug: product.slug,
      skuId,
    }
  }, [product, skuId, progressiveDiscount])

  useEffect(() => {
    const productObject = getProductQueryObject(memoInitialItem)

    const standardObject = getStandardObject(pageData)

    if (
      !standardObject ||
      !productObject ||
      pageData.currentPageType !== PAGE_TYPE_MAP.pdp
    ) {
      return
    }

    if (postalCode) {
      dispatchCepPersistedAppliedEvent({
        pageData,
        cep: postalCode,
        source: 'pdp',
      }).catch(() => {})
    }
  }, [
    product?.items,
    skuId,
    memoInitialItem,
    postalCode,
    product?.categories,
    pageData,
  ])

  useEffect(() => {
    if (productNotFound) {
      return
    }

    const [productItem] = product.items
    const viewItemEvent = makeViewItemEvent(product, productItem)
    const productDetailEvent = makeProductDetailEvent(
      product,
      productItem,
      'pdpView'
    )

    sendEvent(viewItemEvent)
    sendEvent(productDetailEvent)

    const sidebarEl = document.querySelector<HTMLElement>('#sidebar')

    if (!sidebarEl) return

    function updateSidebarTop() {
      const height = sidebarEl!.offsetHeight

      if (height) sidebarEl!.style.top = `calc(100vh - ${height}px)`
    }

    const resizeObserver = new ResizeObserver(updateSidebarTop)

    resizeObserver.observe(sidebarEl)
    updateSidebarTop()

    return () => resizeObserver.disconnect()
  }, [product, productNotFound])

  if (productNotFound) {
    return null
  }

  const breadcrumbList = mapperBreadcrumbList(product.categories)

  const isAlbumProduct = product.productClusters.some(
    (cluster) => cluster.id === ALBUM_PROMO_COLLECTION
  )

  if (isAlbumProduct) {
    const AlbumCms: CustomCMSData | undefined =
      serverData?.albumCMSData?.props?.cmsData

    return (
      <Suspense fallback={null}>
        <AlbumCopaTemplate
          breadcrumbList={breadcrumbList}
          isB2B={isB2B}
          pathname={pathname}
          product={product}
          skuId={skuId}
          pageQuery={data}
          sphereData={sphereData}
          albumCMSData={AlbumCms}
        />
      </Suspense>
    )
  }

  if (product.productId === '11113') {
    return (
      <>
        <GatsbySeo
          title={product.seo.title}
          description={product.seo.description}
          titleTemplate={site?.siteMetadata?.titleTemplate ?? ''}
          canonical={`${site?.siteMetadata?.siteUrl}${pathname}`}
          language="pt-br"
          openGraph={{
            type: 'og:product',
            url: product.seo.slug,
            title: product.seo.title,
            description: product.seo.description,
            images: [product.seo.images[0]],
            site_name: 'Decathlon a maior loja de artigos esportivos',
          }}
        />

        <BreadcrumbJsonLd itemListElements={breadcrumbList.itemListElement} />

        <ProductGroupJsonLd
          product={memoProduct!.product}
          siteUrl={site?.siteMetadata?.siteUrl ?? ''}
          slug={product.slug}
        />

        <ProductProvider {...memoProduct!}>
          <RecommendationProvider
            engines={['dy']}
            product={product}
            skuId={memoInitialItem?.itemId}
            pageName="product"
          >
            <GiftCardProvider>
              <Suspense fallback={null}>
                <div className="max-w-[1280px] m-auto restructure-tablet:pr-md">
                  <div className="hidden restructure-small-desktop:block pb-[24px]">
                    <Breadcrumb
                      breadcrumbList={breadcrumbList.itemListElement}
                      isPdp
                    />
                  </div>
                  <section className="relative flex flex-col flex-wrap md:flex-row gap-x-14 mt-md items-start">
                    {/* Desktop: Imagem grande com carrossel | Mobile: Apenas imagem selecionada */}
                    <section className="w-full md:w-[calc(60%-1.75rem)] md:sticky md:top-24 self-start">
                      {isMobile ? (
                        <GiftCardSelectedImage
                          giftCardComponentsData={giftCardComponentsData}
                        />
                      ) : (
                        <GiftCardImages
                          giftCardComponentsData={giftCardComponentsData}
                        />
                      )}
                    </section>

                    <section
                      id="sidebar"
                      className="
                    w-full max-w-[440px] h-full mx-auto md:w-[calc(40%-1.75rem)] px-4 restructure-tablet:px-0
                    self-start"
                    >
                      <GalleryFlags isPdp />

                      <SidebarGiftCard
                        giftCardComponentsData={giftCardComponentsData}
                        isMobile={isMobile}
                      />
                      {isB2B && <TalkSpecialist />}
                    </section>
                  </section>
                  {giftCardComponentsData && (
                    <GiftCardComponents
                      giftCardComponentsData={giftCardComponentsData}
                    />
                  )}
                </div>
              </Suspense>
            </GiftCardProvider>
          </RecommendationProvider>
        </ProductProvider>
      </>
    )
  }

  return (
    <>
      {memoProduct!.product.sellerInfo?.id === '1' && (
        <ProductStripe stripeData={serverData.stripeData} />
      )}

      <GatsbySeo
        title={product.seo.title}
        description={product.seo.description}
        titleTemplate={site?.siteMetadata?.titleTemplate ?? ''}
        canonical={`${site?.siteMetadata?.siteUrl}${pathname}`}
        language="pt-br"
        openGraph={{
          type: 'og:product',
          url: product.seo.slug,
          title: product.seo.title,
          description: product.seo.description,
          images: [product.seo.images[0]],
          site_name: 'Decathlon a maior loja de artigos esportivos',
        }}
      />

      <BreadcrumbJsonLd itemListElements={breadcrumbList.itemListElement} />

      <ProductGroupJsonLd
        product={memoProduct!.product}
        siteUrl={site?.siteMetadata?.siteUrl ?? ''}
        slug={memoProduct!.product.slug}
      />

      <ProductProvider
        {...memoProduct!}
        kitlooks={serverData.kitlooks?.kits ?? null}
      >
        <RecommendationProvider
          engines={['dy']}
          product={product}
          skuId={memoInitialItem?.itemId}
          pageName="product"
          deferUntilVisible
        >
          <StickyBuyBar />
          <MusePdpEntryPoint />

          <div className="max-w-[1648px] m-auto restructure-tablet:pr-md">
            <section className="flex flex-col flex-wrap md:flex-row gap-x-14">
              <section className="w-full md:w-[calc(60%-1.75rem)]">
                {isPdpFirstFoldRevampEnabled && (
                  <div className="px-4 pt-4 pb-3 restructure-tablet:px-0 restructure-small-desktop:hidden">
                    <ProductTitle
                      showProductCode={false}
                      isFirstFoldRevampEnabled
                    />
                  </div>
                )}
                <GalleryImage
                  isFirstFoldRevampEnabled={isPdpFirstFoldRevampEnabled}
                />
                <div className="hidden restructure-small-desktop:block">
                  <section className="w-full">
                    <ProductDetailsHighlight />
                  </section>
                </div>
              </section>

              <section
                id="sidebar"
                className="top-[150px] w-full md:w-[calc(40%-1.75rem)] restructure-small-desktop:sticky h-max px-4 restructure-tablet:px-0"
              >
                <div className="hidden restructure-small-desktop:block pb-[24px]">
                  <Breadcrumb
                    breadcrumbList={breadcrumbList.itemListElement}
                    isPdp
                  />
                </div>
                <div
                  className={
                    isPdpFirstFoldRevampEnabled
                      ? 'hidden restructure-small-desktop:block'
                      : ''
                  }
                >
                  <GalleryFlags isPdp />
                </div>

                <Sidebar
                  cmsData={bikeInsuranceCms}
                  isPdpFirstFoldRevampEnabled={isPdpFirstFoldRevampEnabled}
                  breadcrumbList={breadcrumbList.itemListElement}
                />
                {isB2B && <TalkSpecialist />}

                <GoToKitlookWidget />
                <TabbedNavigation />
                <div
                  className={
                    isPdpFirstFoldRevampEnabled
                      ? 'hidden restructure-small-desktop:block'
                      : ''
                  }
                >
                  <CommercialBenefits />
                </div>
                <SellerInfo sellerData={product.sellerInfo} />
                <div className="restructure-small-desktop:hidden">
                  <ProductDetailsHighlight />
                </div>
              </section>
            </section>
          </div>

          <div className="mt-6">
            <ProductCustomHtml product={product} />

            <section className="w-full md:mt-[80px]">
              <KitlookWidget
                productId={product.productId}
                kitlooks={serverData.kitlooks?.kits ?? []}
              />
            </section>
            <section className="w-full md:mt-[80px] md:mb-[120px]">
              <ProductInformationSection />
            </section>

            <CrossSellV2 key={product.productId} />

            <SphereContent html={sphereData} pageData={pageData} />

            {!isB2B && <GenericShelfv2 position="middle" engine="dy" />}

            {!isReviewDisplayExperimentEnabled && <CustomerReviews />}

            {!isB2B && <GenericShelfv2 position="bottom" engine="dy" />}

            <InfiniteProductList
              breadcrumbList={breadcrumbList?.itemListElement || []}
            />
          </div>
        </RecommendationProvider>
      </ProductProvider>
    </>
  )
}

/**
 * This query is run during SSG
 * */
export const querySSG = graphql`
  query ProductPage {
    site {
      ...SiteInformation
    }
  }
`

const buildQueryGetProduct = (slug: string) => ({
  query: `{
    DktPdp_GetProductV2(source: "website", slug: ${toGraphqlString(
      slug
    )}, salesChannel: "${isB2B ? 1 : 3}") ${getProductQuery}
  }`,
})

const buildQueryProductDetails = (productId: string, sellerId: string) => ({
  query: `{
    DktPdp_GetProductReview(source: "website", productId: "${productId}", page: 1, range: "0-2") {
      totalReviews
      totalRatingsAverageNote
      reviews {
        id body note firstname countryLabel language rangeAge
      }
      notes {
        one { count countRecommended }
        two { count countRecommended }
        three { count countRecommended }
        four { count countRecommended }
        five { count countRecommended }
      }
    }
    getSellerInfo(sellerId: "${sellerId}") {
      id logo name taxCode description deliveryPolicy
      exchangeReturnPolicy securityPrivacyPolicy startDate startTime
      company address resumeSales resumeSalesDescription
      quantityProducts collectionLinkWeb collectionLinkLinx
    }
  }`,
})

const fetchProduct = async (slug: string) => {
  const QueryGetProduct = buildQueryGetProduct(slug)

  const response = await clientStore.post(
    '/api/io/_v/private/graphql/v1',
    QueryGetProduct,
    {
      headers: { 'Content-Type': 'application/json' },
    }
  )

  if (response.data.errors && response.data.errors.length > 0) {
    const [error] = response.data.errors
    const statusCode = error.extensions?.exception?.status

    if (statusCode === 404) {
      return { DktPdp_GetProductV2: null }
    }

    throw new Error(`GraphQL Error (${statusCode}): ${error.message}`)
  }

  return response.data.data as { DktPdp_GetProductV2: any }
}

const validateProduct = async (
  product: any,
  slug: string,
  CACHE_CONTROL: string
) => {
  if (!product?.DktPdp_GetProductV2?.items?.length) {
    const logger = new MasterDataLogger()

    logger.logRequest({
      context: 'ProductNotFound',
      slug,
      error: JSON.stringify({
        product: product?.DktPdp_GetProductV2,
        items: product?.DktPdp_GetProductV2?.items,
      }),
    })

    const categoryRedirectPath = await getCategoryRedirectPathByProductSlug(
      slug
    )

    if (categoryRedirectPath) {
      return {
        status: 301,
        props: null,
        headers: {
          'cache-control': CACHE_CONTROL,
          location: categoryRedirectPath,
        },
      }
    }

    const params = new URLSearchParams({
      from: encodeURIComponent(`/${slug}/p`),
    })

    return {
      status: 404,
      props: null,
      headers: {
        'cache-control': CACHE_CONTROL,
        location: `/404/?${params.toString()}`,
      },
    }
  }

  const isAlbumProduct = isAlbumPage(
    product.DktPdp_GetProductV2?.productClusters
  )

  const productHasKit = product?.DktPdp_GetProductV2?.items.find(
    (el: any) => el.isKit
  )

  if (productHasKit && !isAlbumProduct) {
    return {
      status: 301,
      props: null,
      headers: {
        'cache-control': CACHE_CONTROL,
        location: `/${slug}/kit`,
      },
    }
  }

  return null
}

const fetchProductDetails = async (productId: string, sellerId: string) => {
  const QueryProductDetails = buildQueryProductDetails(productId, sellerId)

  const { data } = await clientStore.post(
    '/api/io/_v/private/graphql/v1',
    QueryProductDetails,
    {
      headers: { 'Content-Type': 'application/json' },
    }
  )

  return data.data
}

export const getServerData = async ({
  params: { slug },
  query: { skuId },
  query,
}: {
  params: Record<string, string>
  query: Record<string, string>
}) => {
  const CMS_VERSION_ID = query?.versionId ?? ''

  const LEARN_MORE_BIKE_INSURANCE_CMS_PAGE_ID =
    query?.pageId ?? '7ad61af7-f898-41b2-adb5-3a16f37bb5ff'

  const CACHE_CONTROL = 'max-age=300, s-maxage=7200, stale-while-revalidate'

  let currentStep = 'initializing'

  try {
    currentStep = 'fetchProduct'
    const [
      product,
      LearnMoreBikeInsuranceData,
      stripeData,
      giftCardComponentsData,
    ] = await Promise.all([
      retryTransient(() => fetchProduct(slug)),
      getCMSPage({
        contentType: 'global',
        documentId: LEARN_MORE_BIKE_INSURANCE_CMS_PAGE_ID,
        versionId: CMS_VERSION_ID,
        builder: 'faststore',
      }).catch((error) => {
        console.error('Error fetching LearnMoreBikeInsurance CMS:', error)

        return { data: null }
      }),
      getProductStripeData({ CMS_VERSION_ID }).catch((error) => {
        console.error('Error fetching product stripe data:', error)

        return null
      }),
      getGiftCardComponentsData({ CMS_VERSION_ID }).catch((error) => {
        console.error('Error fetching gift card components data:', error)

        return null
      }),
    ])

    currentStep = 'validateProduct'
    const validationError = await validateProduct(product, slug, CACHE_CONTROL)

    if (validationError) {
      return validationError
    }

    const sellerId = product.DktPdp_GetProductV2?.items
      ?.find((item: any) =>
        item?.sellers?.find(
          (seller: any) =>
            seller.sellerDefault && seller.commertialOffer.IsAvailable
        )
      )
      ?.sellers.find((seller: any) => seller.sellerDefault).sellerId

    const sellerIsDecathlon = sellerId === '1'

    const manufacturerCode =
      product.DktPdp_GetProductV2?.items?.[0].manufacturerCode

    const productId = product.DktPdp_GetProductV2?.productId
    const hasProgressiveDiscount =
      product.DktPdp_GetProductV2?.progressiveDiscount

    const isAlbumProduct = isAlbumPage(
      product.DktPdp_GetProductV2?.productClusters
    )

    currentStep = 'fetchProductRelatedData'
    const [
      productDetails,
      sphereData,
      albumCMSData,
      progressiveDiscount,
      productKits,
    ] = await Promise.all([
      fetchProductDetails(productId, sellerId ?? undefined).catch((error) => {
        console.error('Error fetching product details:', error)

        return { DktPdp_GetProductReview: null, getSellerInfo: null }
      }),
      manufacturerCode
        ? axiosClient
            .get(
              `https://sphere-renderer.decathlon.net/content/search?model_codes=${manufacturerCode}&locale=pt_BR&status=1`,
              { responseType: 'text', timeout: 5000 }
            )
            .then((response) => response.data)
            .catch(() => null)
        : Promise.resolve(null),
      isAlbumProduct
        ? getCMSPage({
            contentType: 'product-page',
            documentId: 'd9dbd493-c336-4d42-a1e3-d4a6e49136a7',
            builder: 'faststore',
          }).catch(() => ({ data: null }))
        : Promise.resolve(null),
      hasProgressiveDiscount
        ? clientStore
            .get(
              `/v1/api/decathlon-pdp/progressive-discount/products/${productId}`
            )
            .then((result) => result.data ?? null)
            .catch((error) => {
              console.error('Error fetching progressive discounts:', error)

              return null
            })
        : Promise.resolve(null),
      product.DktPdp_GetProductV2?.productHasKitLook
        ? clientStore
            .get(
              `/v1/api/decathlon-pdp/kitlooks${buildQueryParams(
                'productId',
                product.DktPdp_GetProductV2?.kitLookProductIds ?? []
              )}`
            )
            .then((response) => response.data)
            .catch(() => null)
        : Promise.resolve(null),
    ])

    return {
      status: 200,
      props: {
        LearnMoreBikeInsuranceData,
        giftCardComponentsData,
        albumCMSData,
        stripeData,
        product: {
          slug: slug ?? null,
          ...product.DktPdp_GetProductV2,
          review: sellerIsDecathlon
            ? productDetails?.DktPdp_GetProductReview ?? null
            : null,
          sellerInfo: productDetails?.getSellerInfo ?? null,
        },
        skuId,
        kitlooks: productKits,
        sphereData: sphereData ? encodeURIComponent(sphereData) : null,
        progressiveDiscount,
        pageType: 'pdp',
      },

      headers: { 'cache-control': CACHE_CONTROL },
    }
  } catch (error) {
    const err = error as Error

    console.warn('err =>', err?.message)

    try {
      await sendToSplunk({
        context: 'getServerData:pdp',
        slug: String(slug ?? '/p'),
        step: currentStep,
        message: err?.message,
        stack: err?.stack,
        timestamp: new Date().toISOString(),
        query,
        env: 'master',
      })
    } catch (splunkError) {
      console.error('Failed to send error to Splunk:', splunkError)
    }

    try {
      handleSSRError({
        err,
        query,
        slug: String(slug ?? '/p'),
        stepName: currentStep,
      })
    } catch (handlerError) {
      console.error('Failed to handle SSR error:', handlerError)
    }

    return {
      status: 500,
      props: {},
      headers: { 'cache-control': 'public, max-age=0, must-revalidate' },
    }
  }
}

export default ProductPage
